import { useState } from 'react'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import styled from 'styled-components'
import { test } from 'ramda'
import { useDialogController } from '@machobear/design-system'
import toNumber from 'lodash/toNumber'

import { breakpoints, colors } from '@/styles'
import useCurrentSession from '@/hooks/use-current-session'
import { makeMainLayout } from '@/layouts/main'
import CompaniesSection from '@/features/Pricing/companies-section'
import EnterpriseSection from '@/features/Pricing/enterprise-section'
import FaqSection from '@/features/Pricing/faq-section'
import FeaturesSection from '@/features/Pricing/features-section'
import PricingCards from '@/features/Pricing/pricing-cards'
import {
  AccountType,
  isDowngrade,
  parsePlan,
  Plan,
  planList,
  usePricingPlans
} from '@/features/Pricing/pricing-plans'
import RecapSection from '@/features/Pricing/recap-section'
import TestimonialsSection from '@/features/Pricing/testimonials-section'
import { PricingButton } from '@/features/Pricing/types'
import UpgradeDowngradeModal from '@/features/Pricing/upgrade-downgrade-modal'
import useSetPlans from '@/features/Pricing/use-set-plans'
import { useActivePlan } from '@/features/Register'
import Head from '@/components/head'

const Wrapper = styled.div`
  background-color: ${colors.white};
  width: 100%;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 64px 72px;

  > section {
    margin-bottom: 88px;
  }

  @media (max-width: ${breakpoints.v3Desktop}) {
    padding: 48px 32px;

    > section {
      margin-bottom: 72px;
    }
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    padding: 24px 16px;

    > section {
      margin-bottom: 64px;
    }
  }
`

type StateDataProps = {
  featuredPlan?: AccountType
  featuredPlanText?: string
  hideButtons?: boolean
  footerText?: React.ReactNode
}

type PricingProps = {
  recommendedPlan?: AccountType
}

const Pricing = ({ recommendedPlan }: PricingProps) => {
  const { t } = useTranslation('pricing')
  const { visible, showModal, closeModal } = useDialogController()
  const [clickedPlan, setClickedPlan] = useState<AccountType>('growth')

  const metaTitle = t('pricing:Landlord Pricing Plans | liv.rent')
  const metaDescription = t(
    `pricing:List and market your rental on liv.rent to drive traffic to your listing, screen and find quality tenants, and automate with digital signing and rent collection.`
  )
  const router = useRouter()
  const { query = {} } = router
  const session = useCurrentSession()
  const role = session.user_type
  const isLoggedIn = !!role
  const isLandlord = role === 'landlord'
  const userId = session.id
  const billingUserId = session.company_info?.billing_user_id
  const isBillingUser = toNumber(userId) === toNumber(billingUserId)
  const basePlanName = session?.company_info?.base_plan_name
  const [activeAccountType] = parsePlan(basePlanName)
  const isSpecialPlan = activeAccountType === 'special'

  const { next, loading, active } = useActivePlan()
  const pendingPlanName = next?.plan
  const isActivePlanPromo = test(/coupon/g, active?.plan ?? '')
  const [pendingAccountType] = parsePlan(pendingPlanName)

  const hasPendingPlan = !loading && pendingPlanName && !isActivePlanPromo
  const lastChosenType = hasPendingPlan ? pendingAccountType : activeAccountType
  const planAccountType =
    lastChosenType !== 'special' ? lastChosenType : 'business'

  const { handleReplaceIntendedPlan, downgradeToEssentials, intendedPlanFlag } =
    useSetPlans(router)
  const intendedPlanKey = intendedPlanFlag?.key_value

  const plansData = usePricingPlans()
  const hasRecommendedPlan =
    recommendedPlan && planList.includes(recommendedPlan)
  const plans = planList.map(key => plansData[key])

  const mostPopularText = t('pricing:Most Popular')
  const recommendedText = t('pricing:Recommended')

  const stateData: Record<string, StateDataProps> = {
    loggedRenter: {
      featuredPlan: 'growth',
      featuredPlanText: mostPopularText,
      hideButtons: true,
      footerText: null
    },
    loggedLandlordIncomplete: {
      featuredPlan:
        intendedPlanKey === 'essentials' ? 'growth' : intendedPlanKey,
      featuredPlanText:
        intendedPlanKey === 'essentials' ? mostPopularText : recommendedText,
      hideButtons: false,
      footerText: null
    },
    loggedLandlordComplete: {
      featuredPlan: undefined,
      featuredPlanText: undefined,
      hideButtons: false,
      footerText: (
        <Trans i18nKey="pricing:Looking to change your plan">
          Looking to change your plan?{' '}
          <a
            href="mailto:sales@liv.rent"
            rel="noopener noreferrer"
            target="_blank"
          >
            Contact us
          </a>{' '}
          to find what will work best for you.
        </Trans>
      )
    },
    nonloggedWithRecommenedPlan: {
      featuredPlan: recommendedPlan,
      featuredPlanText: recommendedText,
      hideButtons: false,
      footerText: null
    },
    nonlogged: {
      featuredPlan: 'growth',
      featuredPlanText: mostPopularText,
      hideButtons: false,
      footerText: null
    }
  }

  const getCurrentState = () => {
    if (isLoggedIn) {
      if (!isLandlord) {
        return 'loggedRenter'
      } else if (intendedPlanKey && !isSpecialPlan) {
        return 'loggedLandlordIncomplete'
      } else {
        return 'loggedLandlordComplete'
      }
    } else {
      if (hasRecommendedPlan) {
        return 'nonloggedWithRecommenedPlan'
      } else {
        return 'nonlogged'
      }
    }
  }

  const currentState = getCurrentState()
  const currentData = stateData[currentState]
  const featuredPlan = currentData.featuredPlan
  const featuredText = currentData.featuredPlanText
  const hideButtons = currentData.hideButtons
  const footerText = currentData.footerText

  const handleChangePlan = async (plan: AccountType) => {
    // Don't let modal open until we know if they have a
    // pending plan and change button state
    if (loading) return
    setClickedPlan(plan)
    showModal()
  }

  const getButtonData = (data: Plan, condensed: boolean): PricingButton => {
    const isFree = data.value === 'essentials'
    const isIntendedPlan = data.value === intendedPlanKey
    const isCurrentPlan = data.value === planAccountType
    const buttonData: PricingButton = {
      text: data.buttonText,
      disabled: false
    }

    if (isFree && isLoggedIn && !!intendedPlanKey) {
      buttonData.onClick = downgradeToEssentials
    } else {
      buttonData.href = {
        pathname: '/register',
        query: { ...query, accountType: data.value }
      }
    }

    if (currentState === 'loggedLandlordComplete') {
      if (isCurrentPlan) {
        const active = condensed
          ? t('pricing:Current Plan')
          : t('pricing:My Current Plan')
        const upcoming = condensed
          ? t('pricing:Upcoming Plan')
          : t('pricing:My Upcoming Plan')
        buttonData.text = hasPendingPlan ? upcoming : active
        buttonData.disabled = true
      } else {
        if (isBillingUser) {
          const downgrade = isDowngrade(planAccountType, data.value)
          buttonData.text = downgrade
            ? condensed
              ? t('pricing:Downgrade')
              : t('pricing:Downgrade Your Plan')
            : condensed
            ? t('pricing:Upgrade')
            : t('pricing:Upgrade Your Plan')
          if (downgrade) {
            buttonData.onClick = () => handleChangePlan(data.value)
          }
        } else {
          buttonData.text = condensed
            ? t('pricing:Contact Admin')
            : t('pricing:Contact Plan Administrator')
          buttonData.disabled = true
        }
      }
    } else if (currentState === 'loggedLandlordIncomplete') {
      if (isIntendedPlan) {
        const paidText = condensed
          ? t('pricing:Continue Payment')
          : t('pricing:Continue to Payment')
        buttonData.text = isFree ? buttonData.text : paidText
      } else {
        buttonData.onClick = () => handleReplaceIntendedPlan(data.value)
      }
    }
    return buttonData
  }

  return (
    <>
      <Wrapper>
        <Head title={metaTitle} description={metaDescription} path="/pricing" />
        <Content>
          <PricingCards
            plansData={plansData}
            hideButtons={hideButtons}
            featuredPlan={featuredPlan}
            featuredText={featuredText}
            footerText={footerText}
            getButtonData={getButtonData}
          />
          <CompaniesSection />
          <FeaturesSection plans={plans} />
          <RecapSection
            plans={plans}
            hideButtons={hideButtons}
            featuredPlan={featuredPlan}
            getButtonData={getButtonData}
          />
          <TestimonialsSection />
          <FaqSection />
          <EnterpriseSection />
        </Content>
      </Wrapper>
      <UpgradeDowngradeModal
        visible={visible}
        closeModal={closeModal}
        currentPlan={planAccountType}
        targetPlan={clickedPlan}
        currentPlanEndDate={active?.chargedThroughDate || ''}
        router={router}
      />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  req,
  locale
}) => {
  const { cookies } = req
  const localeWithDefualt = locale || 'en'
  return {
    props: {
      recommendedPlan: cookies['recommendedLandlordPlan'] ?? '',
      ...(await serverSideTranslations(localeWithDefualt, [
        'translation',
        'pricing'
      ]))
    }
  }
}
const headerConfig = {
  showNavItems: true
}

Pricing.Layout = makeMainLayout({ headerConfig })

export default Pricing
